import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell, ClinicalStatCard } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { ServiceBookingListItem, ServiceBookingStatus } from "@/lib/db-service/service-bookings";
import type { ServiceProfessional } from "@/lib/db-service/service-professionals";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PosPagoModal, type PosPagoConfirmPayload } from "@/components/pos/PosPagoModal";
import { PosSaleInvoicePanel } from "@/components/pos/PosSaleInvoicePanel";
import { orderCollectTotal } from "@/lib/pos-collect-order";
import { tryHipRoleAction } from "@/lib/hardware/client";
import {
  resolvePosInvoicingPolicy,
  defaultPosInvoiceMode,
  type PosInvoicingPolicy,
  type PosInvoiceMode,
} from "@/lib/pos/invoicing-policy";
import { NewBookingDialog } from "@/components/services/NewBookingDialog";
import { Plus, Search, Download, ChevronLeft, ChevronRight, ArrowUpDown, ChevronDown, Wallet, CheckCheck } from "lucide-react";
import type { ServiceBookingOrderBy } from "@/lib/db-service/service-bookings";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type MaterializedOrder = {
  id: string;
  precioFinal: number | null;
  precioEstimado: number;
  amountPaid: number | null;
};

const NOT_CHARGEABLE: ServiceBookingStatus[] = ["cancelled", "no_show", "expired"];

export const Route = createFileRoute("/app/turnos")({
  component: TurnosPage,
  head: () => ({ meta: [{ title: "Reservas de servicio" }] }),
});

const STATUS_LABEL: Record<ServiceBookingStatus, string> = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  completed: "Completada",
  rescheduled: "Reprogramada",
  no_show: "No-show",
  expired: "Expirada",
};

function TurnosPage() {
  const tenantId = useWorkspaceTenantId();
  const { productFlags } = useTenantContext();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<ServiceBookingListItem[]>([]);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [professionalId, setProfessionalId] = useState("");
  const [serviceId, setServiceId] = useState("");
  // SCRUM-407: multi-select de estados (BH permite elegir varios a la vez).
  const [statusFilter, setStatusFilter] = useState<ServiceBookingStatus[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [orderBy, setOrderBy] = useState<ServiceBookingOrderBy>("booking_date");
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [detailBooking, setDetailBooking] = useState<ServiceBookingListItem | null>(null);
  // SCRUM-407: marcar seña pagada — dialog propio con "¿confirmar turno
  // también?" tal como pide el ticket, en vez de un patch silencioso.
  const [depositBooking, setDepositBooking] = useState<ServiceBookingListItem | null>(null);
  const [depositAlsoConfirm, setDepositAlsoConfirm] = useState(true);
  const [depositSubmitting, setDepositSubmitting] = useState(false);
  const [detailIntakeAnswers, setDetailIntakeAnswers] = useState<Array<{ label: string; answer: string }> | null>(null);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 50;
  // SCRUM-407: KPIs (En vista/Pendientes/Confirmados) — Agenda ya los tenía,
  // Turnos no. pendingCount/confirmedCount respetan los demás filtros
  // activos (fecha/profesional/servicio/cliente) pero no la página actual
  // ni el filtro de estado en sí (para poder mostrar "3 pendientes" aunque
  // el usuario esté viendo "confirmados").
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [bulkConfirming, setBulkConfirming] = useState(false);

  const posCollectEnabled = productFlags?.payment_pos_mode === true;
  const [cobrandoBookingId, setCobrandoBookingId] = useState<string | null>(null);
  const [collectOrder, setCollectOrder] = useState<MaterializedOrder | null>(null);
  const [collectProcesando, setCollectProcesando] = useState(false);
  const [paymentTerminalBound, setPaymentTerminalBound] = useState(false);
  const [invoicingPolicy, setInvoicingPolicy] = useState<PosInvoicingPolicy>("optional");
  const [invoiceMode, setInvoiceMode] = useState<PosInvoiceMode>("none");
  const [lastCollectedOrder, setLastCollectedOrder] = useState<{
    orderId: string;
    invoiceSummary?: string | null;
    customerEmail?: string | null;
    customerName?: string | null;
  } | null>(null);
  const invoicingEnabled =
    productFlags?.invoicing_afip_direct === true || productFlags?.invoicing_tusfacturas === true;
  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    apiGetJson<{ bindings?: { paymentTerminal?: string } }>("/api/hardware/bindings", tenantId)
      .then((r) => setPaymentTerminalBound(Boolean(r.bindings?.paymentTerminal)))
      .catch(() => setPaymentTerminalBound(false));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    apiGetJson<{ config?: { storePolicies?: Record<string, unknown> } | null }>(
      "/api/tenant/config",
      tenantId,
    )
      .then((r) => {
        const policy = resolvePosInvoicingPolicy(r.config?.storePolicies ?? {});
        setInvoicingPolicy(policy);
        setInvoiceMode(defaultPosInvoiceMode(policy));
      })
      .catch(() => {});
  }, [tenantId]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (professionalId) qs.set("professionalId", professionalId);
      if (serviceId) qs.set("serviceId", serviceId);
      if (statusFilter.length > 0) qs.set("status", statusFilter.join(","));
      if (dateFrom) qs.set("dateFrom", dateFrom);
      if (dateTo) qs.set("dateTo", dateTo);
      if (customerQuery.trim()) qs.set("customerQuery", customerQuery.trim());
      qs.set("orderBy", orderBy);
      qs.set("orderDir", orderDir);
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(page * PAGE_SIZE));
      // SCRUM-407: KPIs de Pendientes/Confirmados — mismos filtros de
      // fecha/profesional/servicio/cliente que la lista, pero status fijo
      // (independiente de qué esté eligiendo el usuario en el filtro), y
      // limit=1 porque solo hace falta el total, no las filas.
      const kpiQs = (forcedStatus: ServiceBookingStatus) => {
        const kq = new URLSearchParams(qs);
        kq.set("status", forcedStatus);
        kq.set("limit", "1");
        kq.set("offset", "0");
        return kq;
      };
      const [b, p, s, pendingRes, confirmedRes] = await Promise.all([
        apiGetJson<{ bookings: ServiceBookingListItem[]; total: number }>(
          `/api/service-bookings?${qs.toString()}`,
          tenantId,
        ),
        apiGetJson<{ professionals: ServiceProfessional[] }>(
          "/api/service-bookings?resource=professionals",
          tenantId,
        ),
        apiGetJson<{ services: ServiceCatalogItem[] }>(
          "/api/service-bookings?resource=services",
          tenantId,
        ),
        apiGetJson<{ total: number }>(`/api/service-bookings?${kpiQs("pending").toString()}`, tenantId),
        apiGetJson<{ total: number }>(`/api/service-bookings?${kpiQs("confirmed").toString()}`, tenantId),
      ]);
      setBookings(b.bookings ?? []);
      setTotal(b.total ?? 0);
      setProfessionals(p.professionals ?? []);
      setServices(s.services ?? []);
      setPendingCount(pendingRes.total ?? 0);
      setConfirmedCount(confirmedRes.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, professionalId, serviceId, statusFilter, dateFrom, dateTo, customerQuery, orderBy, orderDir, page]);

  // SCRUM-407: confirmar todos los turnos pendientes que matchean el filtro
  // actual, sin tener que confirmarlos de a uno. Reusa el mismo PATCH que ya
  // existe (patchStatus) — no crea un endpoint de bulk aparte, para no
  // duplicar la lógica de notificación por transición de estado.
  async function bulkConfirmPending() {
    if (!tenantId || pendingCount === 0) return;
    setBulkConfirming(true);
    try {
      const qs = new URLSearchParams();
      if (professionalId) qs.set("professionalId", professionalId);
      if (serviceId) qs.set("serviceId", serviceId);
      if (dateFrom) qs.set("dateFrom", dateFrom);
      if (dateTo) qs.set("dateTo", dateTo);
      if (customerQuery.trim()) qs.set("customerQuery", customerQuery.trim());
      qs.set("status", "pending");
      qs.set("limit", "200");
      const pendingList = await apiGetJson<{ bookings: ServiceBookingListItem[] }>(
        `/api/service-bookings?${qs.toString()}`,
        tenantId,
      );
      const ids = (pendingList.bookings ?? []).map((b) => b.id);
      await Promise.all(
        ids.map((id) => apiSendJson(`/api/service-bookings?id=${id}`, "PATCH", { status: "confirmed" }, tenantId)),
      );
      toast.success(`${ids.length} turno${ids.length === 1 ? "" : "s"} confirmado${ids.length === 1 ? "" : "s"}`);
      if (pendingCount > 200) {
        toast.info("Había más de 200 pendientes — confirmá el resto en otra tanda.");
      }
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudieron confirmar todos");
    } finally {
      setBulkConfirming(false);
    }
  }

  // Cualquier cambio de filtro/orden vuelve a la primera página.
  useEffect(() => {
    setPage(0);
  }, [professionalId, serviceId, statusFilter, dateFrom, dateTo, customerQuery, orderBy, orderDir]);

  useEffect(() => {
    void load();
  }, [load]);

  // SCRUM-423: respuestas del formulario intake para el turno en detalle
  // (personal autorizado, visto desde el mismo dialog de detalle).
  useEffect(() => {
    if (!detailBooking || !tenantId) {
      setDetailIntakeAnswers(null);
      return;
    }
    apiGetJson<{ response: { answers: Array<{ label: string; answer: string }> } | null }>(
      `/api/service-admin?resource=intake-responses&bookingId=${detailBooking.id}`,
      tenantId,
    )
      .then((r) => setDetailIntakeAnswers(r.response?.answers ?? null))
      .catch(() => setDetailIntakeAnswers(null));
  }, [detailBooking, tenantId]);

  function toggleSort(col: ServiceBookingOrderBy) {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  }

  // SCRUM-421/427: export operativo — mismo patrón que api.ops.export.orders.ts
  // (permisos + límite de filas), pero para sm_bookings.
  async function exportBookings() {
    if (!tenantId) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (professionalId) qs.set("professionalId", professionalId);
      if (serviceId) qs.set("serviceId", serviceId);
      if (statusFilter.length > 0) qs.set("status", statusFilter.join(","));
      if (dateFrom) qs.set("dateFrom", dateFrom);
      if (dateTo) qs.set("dateTo", dateTo);
      if (customerQuery.trim()) qs.set("customerQuery", customerQuery.trim());
      const res = await fetch(`/api/ops/export/bookings?${qs.toString()}`, {
        headers: { "X-Tenant-Id": tenantId },
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "No se pudo exportar");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `turnos-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  }

  async function confirmDepositPaid() {
    if (!depositBooking || !tenantId) return;
    setDepositSubmitting(true);
    try {
      await apiSendJson(
        `/api/service-bookings?id=${depositBooking.id}`,
        "PATCH",
        {
          depositPaid: true,
          ...(depositAlsoConfirm && depositBooking.status === "pending" ? { status: "confirmed" } : {}),
        },
        tenantId,
      );
      toast.success("Seña marcada como pagada");
      setDepositBooking(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo marcar la seña");
    } finally {
      setDepositSubmitting(false);
    }
  }

  async function patchStatus(id: string, next: ServiceBookingStatus) {
    try {
      await apiSendJson(`/api/service-bookings?id=${id}`, "PATCH", { status: next }, tenantId);
      toast.success("Turno actualizado");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar");
    }
  }

  // SERV-GAP-10 — reprogramar turno
  const [reschedBooking, setReschedBooking] = useState<ServiceBookingListItem | null>(null);
  const [reschedDate, setReschedDate] = useState("");
  const [reschedSlots, setReschedSlots] = useState<string[]>([]);
  const [reschedLoadingSlots, setReschedLoadingSlots] = useState(false);
  const [reschedSubmitting, setReschedSubmitting] = useState(false);

  function openReschedule(booking: ServiceBookingListItem) {
    setReschedBooking(booking);
    setReschedDate(booking.bookingDate);
    setReschedSlots([]);
  }

  useEffect(() => {
    if (!reschedBooking || !reschedDate || !tenantId) return;
    setReschedLoadingSlots(true);
    apiGetJson<{ slots?: string[] }>(
      `/api/service-bookings?resource=slots&professionalId=${reschedBooking.professionalId}&serviceId=${reschedBooking.serviceId}&date=${reschedDate}&excludeBookingId=${reschedBooking.id}`,
      tenantId,
    )
      .then((r) => setReschedSlots(r.slots ?? []))
      .catch(() => setReschedSlots([]))
      .finally(() => setReschedLoadingSlots(false));
  }, [reschedBooking, reschedDate, tenantId]);

  async function confirmReschedule(startTime: string) {
    if (!reschedBooking || !tenantId) return;
    setReschedSubmitting(true);
    try {
      const r = await apiSendJson<{ error?: string }>(
        `/api/service-bookings?id=${reschedBooking.id}`,
        "PATCH",
        { action: "reschedule", bookingDate: reschedDate, startTime },
        tenantId,
      );
      if (r.error) {
        toast.error(
          r.error === "SLOT_UNAVAILABLE" ? "Ese horario ya no está disponible" : "No se pudo reprogramar",
        );
        return;
      }
      toast.success("Turno reprogramado");
      setReschedBooking(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo reprogramar");
    } finally {
      setReschedSubmitting(false);
    }
  }

  async function openCobrar(bookingId: string) {
    if (!tenantId) return;
    try {
      const r = await apiSendJson<{ order?: MaterializedOrder; error?: string }>(
        "/api/service-bookings",
        "POST",
        { action: "materialize_order", bookingId },
        tenantId,
      );
      if (!r.order) {
        toast.error(r.error || "No se pudo generar la orden de cobro");
        return;
      }
      setCollectOrder(r.order);
      setCobrandoBookingId(bookingId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo generar la orden de cobro");
    }
  }

  async function handleCollectConfirm(payload: PosPagoConfirmPayload) {
    if (!tenantId || !collectOrder) return;
    setCollectProcesando(true);
    try {
      const sess = await apiGetJson<{ session?: { id: string }; _lock?: unknown }>(
        "/api/pos/session",
        tenantId,
      );
      if (sess._lock) {
        toast.error("POS no disponible en este plan");
        return;
      }
      const sessionId = sess.session?.id;
      if (!sessionId) {
        toast.error("Abrí una sesión de caja en POS para cobrar este turno");
        return;
      }
      const r = await apiSendJson<{
        ok?: boolean;
        error?: string;
        invoice?: { comprobanteNro?: string | null; cae?: string | null; letra?: string | null } | null;
      }>(
        "/api/pos/pay-order",
        "POST",
        {
          sessionId,
          orderId: collectOrder.id,
          paymentMethods:
            payload.paymentMethods && payload.paymentMethods.length > 0
              ? payload.paymentMethods
              : [{ method: payload.metodo, amount: payload.montoPagado }],
          giftCardCode: payload.codigoGC || undefined,
          loyaltyPoints: payload.loyaltyPoints || undefined,
          customerId: payload.customerId || undefined,
          changeGiven: payload.vuelto,
          invoiceMode: payload.invoiceMode ?? invoiceMode,
          manualAck: payload.manualAck,
        },
        tenantId,
      );
      if (r.error) {
        toast.error(r.error);
        return;
      }
      toast.success("Cobro registrado en caja");
      const booking = bookings.find((b) => b.id === cobrandoBookingId);
      setLastCollectedOrder({
        orderId: collectOrder.id,
        invoiceSummary: r.invoice
          ? `Factura ${r.invoice.letra ?? ""}${r.invoice.comprobanteNro ? ` N° ${r.invoice.comprobanteNro}` : ""} emitida`.replace(/\s+/g, " ")
          : null,
        customerEmail: booking?.customerEmail ?? null,
        customerName: booking?.customerName ?? null,
      });
      setCobrandoBookingId(null);
      setCollectOrder(null);
      await load();
      if (payload.metodo === "efectivo") {
        void tryHipRoleAction(tenantId, "cashDrawer", "openDrawer");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al cobrar");
    } finally {
      setCollectProcesando(false);
    }
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="turnos" />
      <CrudShellHeader
        title="Reservas"
        count={total}
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-2" disabled={exporting} onClick={() => void exportBookings()}>
              <Download className="h-4 w-4" /> {exporting ? "Exportando…" : "Exportar"}
            </Button>
            <Button size="sm" className="gap-2" onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" /> Nuevo turno
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-2 gap-2 px-4 pb-2 sm:grid-cols-6">
        <div className="relative col-span-2 sm:col-span-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Buscar cliente…"
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
            data-testid="bookings-customer-search"
          />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={professionalId}
          onChange={(e) => setProfessionalId(e.target.value)}
        >
          <option value="">Profesional</option>
          {professionals.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
        >
          <option value="">Servicio</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 justify-between font-normal">
              {statusFilter.length === 0
                ? "Estado"
                : statusFilter.length === 1
                  ? STATUS_LABEL[statusFilter[0]!]
                  : `${statusFilter.length} estados`}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-1">
              {Object.entries(STATUS_LABEL).map(([k, v]) => {
                const key = k as ServiceBookingStatus;
                const checked = statusFilter.includes(key);
                return (
                  <label key={k} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        setStatusFilter((prev) => (v ? [...prev, key] : prev.filter((s) => s !== key)))
                      }
                    />
                    {v}
                  </label>
                );
              })}
              {statusFilter.length > 0 ? (
                <button
                  type="button"
                  className="w-full pt-1 text-left text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setStatusFilter([])}
                >
                  Limpiar selección
                </button>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-3 gap-2 px-4 pb-3">
        <ClinicalStatCard label="En vista" value={total} />
        <ClinicalStatCard label="Pendientes" value={pendingCount} variant={pendingCount > 0 ? "warning" : "default"} />
        <ClinicalStatCard label="Confirmados" value={confirmedCount} variant="success" />
      </div>
      {statusFilter.includes("pending") && pendingCount > 0 ? (
        <div className="px-4 pb-3">
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            disabled={bulkConfirming}
            onClick={() => void bulkConfirmPending()}
          >
            <CheckCheck className="h-4 w-4" />
            {bulkConfirming ? "Confirmando…" : `Confirmar ${pendingCount} pendiente${pendingCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      ) : null}
      {loading ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto px-4 pb-8">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-2">
                  <button type="button" className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort("booking_date")}>
                    Fecha <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="py-2 pr-2">Hora</th>
                <th className="py-2 pr-2">
                  <button type="button" className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort("customer_name")}>
                    Cliente <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="py-2 pr-2">Servicio</th>
                <th className="py-2 pr-2">Profesional</th>
                <th className="py-2 pr-2">Estado</th>
                <th className="py-2 pr-2">Seña</th>
                <th className="py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b">
                  <td className="py-2 pr-2">{b.bookingDate}</td>
                  <td className="py-2 pr-2">{b.startTime}</td>
                  <td className="py-2 pr-2">
                    <button
                      type="button"
                      className="text-left underline-offset-2 hover:underline"
                      onClick={() => setDetailBooking(b)}
                      data-testid="booking-detail-trigger"
                    >
                      {b.customerName}
                    </button>
                  </td>
                  <td className="py-2 pr-2">{b.serviceName ?? "—"}</td>
                  <td className="py-2 pr-2">{b.professionalName ?? "—"}</td>
                  <td className="py-2 pr-2">{STATUS_LABEL[b.status]}</td>
                  <td className="py-2 pr-2">
                    {b.serviceDepositRequired ? (
                      b.depositPaid ? (
                        <span className="text-xs font-medium text-emerald-600">Pagada</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 px-2 text-xs"
                          onClick={() => {
                            setDepositAlsoConfirm(true);
                            setDepositBooking(b);
                          }}
                        >
                          <Wallet className="h-3 w-3" /> Marcar seña
                        </Button>
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {posCollectEnabled && !NOT_CHARGEABLE.includes(b.status) && (
                        <Button size="sm" onClick={() => void openCobrar(b.id)}>
                          Cobrar
                        </Button>
                      )}
                      {b.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => void patchStatus(b.id, "confirmed")}>
                          Confirmar
                        </Button>
                      )}
                      {(b.status === "pending" || b.status === "confirmed" || b.status === "rescheduled") && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => void patchStatus(b.id, "completed")}>
                            Completar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => void patchStatus(b.id, "no_show")}>
                            No-show
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openReschedule(b)}>
                            Reprogramar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={cn("text-rose-700")}
                            onClick={() => void patchStatus(b.id, "cancelled")}
                          >
                            Cancelar
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {bookings.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No hay turnos con estos filtros.</p>
          )}
          {total > 0 ? (
            <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
              <span>
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
      {cobrandoBookingId && collectOrder && tenantId ? (
        <PosPagoModal
          total={orderCollectTotal(collectOrder)}
          tenantId={tenantId}
          flags={productFlags ?? {}}
          paymentTerminalBound={paymentTerminalBound}
          procesando={collectProcesando}
          orderId={collectOrder.id}
          description="Cobro de turno"
          invoiceMode={invoiceMode}
          onInvoiceModeChange={setInvoiceMode}
          invoicingPolicy={invoicingPolicy}
          showInvoicing
          onClose={() => {
            if (!collectProcesando) {
              setCobrandoBookingId(null);
              setCollectOrder(null);
            }
          }}
          onConfirm={(p) => void handleCollectConfirm(p)}
        />
      ) : null}
      <Dialog
        open={Boolean(lastCollectedOrder)}
        onOpenChange={(open) => {
          if (!open) setLastCollectedOrder(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cobro registrado</DialogTitle>
          </DialogHeader>
          {lastCollectedOrder && tenantId ? (
            invoicingEnabled ? (
              <PosSaleInvoicePanel
                tenantId={tenantId}
                orderId={lastCollectedOrder.orderId}
                policy={invoicingPolicy}
                initialSummary={lastCollectedOrder.invoiceSummary}
                customerEmail={lastCollectedOrder.customerEmail}
                customerName={lastCollectedOrder.customerName}
              />
            ) : (
              <p className="text-sm text-muted-foreground">El turno quedó cobrado.</p>
            )
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(reschedBooking)}
        onOpenChange={(open) => {
          if (!open) setReschedBooking(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprogramar turno</DialogTitle>
          </DialogHeader>
          {reschedBooking ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {reschedBooking.customerName} · {reschedBooking.serviceName ?? "Servicio"}
              </p>
              <Input
                type="date"
                value={reschedDate}
                onChange={(e) => setReschedDate(e.target.value)}
              />
              {reschedLoadingSlots ? (
                <p className="text-sm text-muted-foreground">Buscando horarios disponibles…</p>
              ) : reschedSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin horarios disponibles ese día.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {reschedSlots.map((slot) => (
                    <Button
                      key={slot}
                      size="sm"
                      variant="outline"
                      disabled={reschedSubmitting}
                      onClick={() => void confirmReschedule(slot)}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={depositBooking != null} onOpenChange={(v) => !v && setDepositBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar seña pagada</DialogTitle>
          </DialogHeader>
          {depositBooking ? (
            <div className="space-y-4 text-sm">
              <p>
                {depositBooking.customerName} · {depositBooking.serviceName ?? "Servicio"}
                {depositBooking.serviceDepositAmount != null ? (
                  <span className="text-muted-foreground"> · seña ${depositBooking.serviceDepositAmount}</span>
                ) : null}
              </p>
              {depositBooking.status === "pending" ? (
                <label className="flex items-center gap-2">
                  <Checkbox checked={depositAlsoConfirm} onCheckedChange={(v) => setDepositAlsoConfirm(Boolean(v))} />
                  Confirmar el turno también
                </label>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setDepositBooking(null)}>
                  Cancelar
                </Button>
                <Button size="sm" disabled={depositSubmitting} onClick={() => void confirmDepositPaid()}>
                  {depositSubmitting ? "Guardando…" : "Marcar como pagada"}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {tenantId ? (
        <NewBookingDialog
          open={newOpen}
          onOpenChange={setNewOpen}
          tenantId={tenantId}
          professionals={professionals}
          services={services}
          onCreated={() => void load()}
        />
      ) : null}
      <Dialog open={detailBooking != null} onOpenChange={(v) => !v && setDetailBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalle del turno</DialogTitle>
          </DialogHeader>
          {detailBooking ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">{detailBooking.customerName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Estado</p>
                  <p className="font-medium">{STATUS_LABEL[detailBooking.status]}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p>{detailBooking.customerEmail}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Teléfono</p>
                  <p>{detailBooking.customerPhone ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Servicio</p>
                  <p>{detailBooking.serviceName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Profesional</p>
                  <p>{detailBooking.professionalName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fecha y hora</p>
                  <p>{detailBooking.bookingDate} · {detailBooking.startTime}–{detailBooking.endTime}</p>
                </div>
                {detailBooking.cancellationWindowHours ? (
                  <div>
                    <p className="text-xs text-muted-foreground">Ventana de cancelación</p>
                    <p>{detailBooking.cancellationWindowHours} hs de anticipación</p>
                  </div>
                ) : null}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Notas</p>
                <p className="whitespace-pre-wrap">{detailBooking.notes || "Sin notas."}</p>
              </div>
              {detailIntakeAnswers && detailIntakeAnswers.length > 0 ? (
                <div className="space-y-1.5 border-t pt-3" data-testid="booking-intake-answers">
                  <p className="text-xs font-medium text-muted-foreground">Formulario previo al turno</p>
                  {detailIntakeAnswers.map((a, i) => (
                    <div key={i} className="text-sm">
                      <span className="text-muted-foreground">{a.label}: </span>
                      <span className="font-medium">{a.answer}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </ClinicalPageShell>
  );
}
